/**
 * A small fuzzy matcher for short lists (no dependency). Ranking: prefix > word start > anywhere in
 * a row > subsequence. A score is higher for a better match; `null` is no match.
 */
const norm = (value: string): string => value.toLowerCase().replace(/\s+/g, " ").trim();

const isWordStart = (text: string, index: number): boolean => index === 0 || /[\s\-_./·:]/.test(text[index - 1]);

/** Index of the first occurrence of `needle` in `text` that starts a word, or -1. */
const wordStartIndex = (text: string, needle: string): number => {
  let from = 0;
  for (;;) {
    const at = text.indexOf(needle, from);
    if (at < 0) return -1;
    if (isWordStart(text, at)) return at;
    from = at + 1;
  }
};

/** Higher is better; `null` means the query does not match the text. An empty query matches with 0. */
export function fuzzyScore(query: string, text: string): number | null {
  const q = norm(query);
  const t = norm(text);
  if (!q) return 0;
  if (!t) return null;
  if (t.startsWith(q)) return 4000 - (t.length - q.length);
  const word = wordStartIndex(t, q);
  if (word >= 0) return 3000 - word;
  // Every query word starts a word of the text, in any order: "chart edit" finds "Edit a chart".
  const tokens = q.split(" ");
  if (tokens.length > 1 && tokens.every((token) => wordStartIndex(t, token) >= 0)) return 2500 - t.length;
  const inside = t.indexOf(q);
  if (inside >= 0) return 2000 - inside;
  // Subsequence: the letters appear in order; fewer gaps and an earlier start score higher.
  let at = -1;
  let gaps = 0;
  for (const ch of q.replace(/ /g, "")) {
    const next = t.indexOf(ch, at + 1);
    if (next < 0) return null;
    if (at >= 0) gaps += next - at - 1;
    at = next;
  }
  return 1000 - gaps - Math.max(0, at - q.length);
}

/** Keywords rank a little below the same hit on the label. */
const KEYWORD_PENALTY = 50;

/**
 * Items that match, best first. A tie keeps the input order. `texts` returns the label first, then
 * any keywords. An empty query returns the items unchanged.
 */
export function rankByFuzzy<T>(items: readonly T[], query: string, texts: (item: T) => readonly string[]): T[] {
  if (!norm(query)) return [...items];
  const scored: { item: T; score: number; order: number }[] = [];
  items.forEach((item, order) => {
    let best: number | null = null;
    texts(item).forEach((text, i) => {
      const score = fuzzyScore(query, text);
      if (score === null) return;
      const adjusted = i === 0 ? score : score - KEYWORD_PENALTY;
      if (best === null || adjusted > best) best = adjusted;
    });
    if (best !== null) scored.push({ item, score: best, order });
  });
  return scored.sort((a, b) => b.score - a.score || a.order - b.order).map((entry) => entry.item);
}
